import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import { Popover, Space, Switch, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import { RoundInput } from 'components/Custom/inputs';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { formatDisplayDate } from 'i18n/calendarLabels';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import {
  deleteRuleRequest,
  enableDisableRuleRequest,
  saveRuleRequest,
  updateRuleRequest,
} from 'store/receipt-rules/actions';
import { getReceiptRulesForConnection } from 'store/receipt-rules/selectors';
import {
  DrawerStatsBadge,
  DrawerToolbar,
  DrawerWrapper,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  ExtractionRulesDrawerStyle,
  HeaderActions,
  ToolbarActionButton,
  RuleListItem,
  RuleListItemHeader,
  RuleListItemLeft,
  RuleListItemContent,
  RuleListItemMetaDate,
  RuleListItemTextColumn,
  RuleListItemTitle,
  RuleListItemRight,
  RulesListContainer,
  RulesListPanel,
  RulesListWrapper,
  RulesPaginationRow,
  RulesSearchNoResults,
  RulesSearchRow,
} from './extraction-rules-styles';
import {
  buildEmptyRuleDraft,
  InterpretationRuleModal,
} from './InterpretationRuleModal';
import {
  buildInterpretationRuleFieldOptionsFromOrganisation,
  filterCopyRuleTargetOrganisations,
  openCopyRuleToEntityPicker,
} from './open-interpretation-rules';
import { createRuleModalInstanceKey, cloneRuleForCopy } from './rule-modal-helpers';
import { formatRulePreview } from './rule-preview';
import { fetchCountriesRequest } from 'store/common/actions';
import { getCommonCountries } from 'store/common/selectors';
import {
  getOrganisationViesContacts,
  getOrgOrganisations,
  getReceiptStatisticsAll,
} from 'store/organisation/selectors';
import type { InterpretationRule } from './types';
import { buildReceiptStatsByConnectionId } from 'utils/receipt-statistics';

const RULE_LIST_PAGE_SIZE = 10;

export interface InterpretationRulesDrawerProps {
  organisationId: string;
}

export const InterpretationRulesDrawer: React.FC<
  InterpretationRulesDrawerProps
> = ({ organisationId }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const rules = useSelector(getReceiptRulesForConnection) || [];
  const organisations = useSelector(getOrgOrganisations);
  const receiptStatistics = useSelector(getReceiptStatisticsAll);
  const countries = useSelector(getCommonCountries);
  const organisationViesContactsSlice = useSelector(
    getOrganisationViesContacts
  );

  const organisation = React.useMemo(
    () => organisations?.find((org) => org.uuid === organisationId),
    [organisations, organisationId]
  );

  const viesContacts = React.useMemo(() => {
    const connectionId = organisation?.connectionId;
    if (!connectionId?.trim() || !organisationViesContactsSlice) {
      return undefined;
    }
    if (organisationViesContactsSlice.connectionId !== connectionId) {
      return undefined;
    }
    return organisationViesContactsSlice.contacts;
  }, [organisation?.connectionId, organisationViesContactsSlice]);

  React.useEffect(() => {
    if (!countries?.length) {
      dispatch(fetchCountriesRequest());
    }
  }, [countries, dispatch]);

  /** Uses organisation + contacts + countries already in Redux. */
  const fieldOptions = React.useMemo(
    () =>
      buildInterpretationRuleFieldOptionsFromOrganisation(
        organisation,
        viesContacts,
        countries,
        i18n.language
      ),
    [organisation, viesContacts, countries, i18n.language]
  );

  const [openMenuRuleId, setOpenMenuRuleId] = React.useState<string | null>(
    null
  );
  const statsByConnectionId = React.useMemo(
    () => buildReceiptStatsByConnectionId(receiptStatistics),
    [receiptStatistics]
  );

  const copyTargetOrganisations = React.useMemo(
    () =>
      filterCopyRuleTargetOrganisations(
        organisations,
        organisationId,
        statsByConnectionId
      ),
    [organisations, organisationId, statsByConnectionId]
  );
  const hasCopyTargets = copyTargetOrganisations.length > 0;
  const [ruleSearchQuery, setRuleSearchQuery] = React.useState('');
  const [ruleListCurrentPage, setRuleListCurrentPage] = React.useState(1);
  const rulesListViewportRef = React.useRef<HTMLDivElement | null>(null);

  const closeRuleMenu = React.useCallback(() => {
    setOpenMenuRuleId(null);
  }, []);

  const handleSaveRule = (rule: InterpretationRule) => {
    if (rule.id) {
      dispatch(updateRuleRequest(organisationId, rule, rule.id));
    } else {
      dispatch(saveRuleRequest(organisationId, rule));
    }
  };

  const openRuleModal = React.useCallback(
    (
      initialRule?: Partial<InterpretationRule>,
      initialMode?: 'describe' | 'build'
    ) => {
      dispatch(
        turnModalOn({
          title: initialRule?.id
            ? t('organisation.extraction-rules.edit-rule-title')
            : t('organisation.extraction-rules.create-rule-title'),
          type: WindowType.WARNING,
          fullHeight: true,
          component: (
            <InterpretationRuleModal
              key={createRuleModalInstanceKey()}
              organisationId={organisationId}
              fieldOptions={fieldOptions}
              existingRules={rules}
              initialRule={initialRule}
              initialMode={
                initialMode ?? (initialRule?.id ? 'build' : 'describe')
              }
              expandAllSteps={true}
              onSave={async (rule) => handleSaveRule(rule)}
            />
          ),
        })
      );
    },
    [dispatch, fieldOptions, organisationId, t]
  );

  const handleCopyTo = (rule: InterpretationRule) => {
    closeRuleMenu();
    openCopyRuleToEntityPicker({
      dispatch,
      t,
      sourceOrganisationId: organisationId,
      rule,
      organisations: organisations ?? [],
      countries,
      statsByConnectionId,
    });
  };

  const handleToggle = async (ruleId: string, enabled: boolean) => {
    dispatch(enableDisableRuleRequest(organisationId, ruleId, enabled));
  };

  const handleDelete = async (ruleId: string) => {
    dispatch(deleteRuleRequest(organisationId, ruleId));
    closeRuleMenu();
  };

  const handleDuplicate = (rule: InterpretationRule) => {
    closeRuleMenu();
    openRuleModal(
      cloneRuleForCopy(rule, t('organisation.extraction-rules.duplicate')),
      'build'
    );
  };

  const renderRuleContextMenu = (rule: InterpretationRule) => (
    <div className="context-menu-items">
      <Space direction="vertical" style={{ rowGap: '0' }}>
        <IconContextButton
          type="text"
          icon={<EditOutlined style={{ width: '18px', height: '18px' }} />}
          onClick={() => {
            closeRuleMenu();
            openRuleModal(rule, 'build');
          }}
        >
          {t('common.edit')}
        </IconContextButton>
        <Tooltip
          title={
            hasCopyTargets
              ? undefined
              : t('organisation.extraction-rules.copy-to-no-entities')
          }
        >
          <span>
            <IconContextButton
              type="text"
              disabled={!hasCopyTargets}
              icon={<CopyOutlined style={{ width: '18px', height: '18px' }} />}
              onClick={() => handleCopyTo(rule)}
            >
              {t('organisation.extraction-rules.copy-to')}
            </IconContextButton>
          </span>
        </Tooltip>
        <IconContextButton
          type="text"
          icon={<SnippetsOutlined style={{ width: '18px', height: '18px' }} />}
          onClick={() => handleDuplicate(rule)}
        >
          {t('organisation.extraction-rules.duplicate')}
        </IconContextButton>
        <StyledPopconfirm
          title={t('organisation.extraction-rules.delete-confirm')}
          onConfirm={() => void handleDelete(rule.id)}
          okText={t('common.delete')}
          cancelText={t('common.cancel')}
          placement="topRight"
          zIndex={1400}
        >
          <IconContextButton
            type="text"
            icon={<DeleteOutlined style={{ width: '18px', height: '18px' }} />}
          >
            {t('common.delete')}
          </IconContextButton>
        </StyledPopconfirm>
      </Space>
    </div>
  );

  const activeCount = rules.filter((r) => r.enabled).length;

  const rulePreviewById = React.useMemo(() => {
    const previews = new Map<string, string>();
    rules.forEach((rule) => {
      previews.set(rule.id, formatRulePreview(rule, t, fieldOptions));
    });
    return previews;
  }, [rules, t, fieldOptions]);

  const hasAnyRules = rules.length > 0;

  const filteredRules = React.useMemo(() => {
    const q = ruleSearchQuery.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((rule) => {
      const preview = rulePreviewById.get(rule.id) ?? '';
      return (
        rule.name.toLowerCase().includes(q) ||
        preview.toLowerCase().includes(q)
      );
    });
  }, [rulePreviewById, ruleSearchQuery, rules]);

  const filteredItemCount = filteredRules.length;

  const ruleListTotalPages = Math.max(
    1,
    Math.ceil(filteredItemCount / RULE_LIST_PAGE_SIZE)
  );

  const paginatedRules = filteredRules.slice(
    (ruleListCurrentPage - 1) * RULE_LIST_PAGE_SIZE,
    ruleListCurrentPage * RULE_LIST_PAGE_SIZE
  );

  React.useEffect(() => {
    setRuleListCurrentPage(1);
  }, [ruleSearchQuery]);

  React.useEffect(() => {
    setRuleListCurrentPage((currentPage) =>
      Math.min(currentPage, ruleListTotalPages)
    );
  }, [ruleListTotalPages]);

  React.useEffect(() => {
    const rulesListViewport = rulesListViewportRef.current;
    if (!rulesListViewport) return;
    rulesListViewport.scrollTop = 0;
  }, [ruleListCurrentPage, ruleSearchQuery]);

  const renderRuleRow = (rule: InterpretationRule) => {
    const preview = formatRulePreview(rule, t, fieldOptions);
    const displayName = rule.name?.trim() || preview || t('organisation.extraction-rules.default-rule-name');
    const createdOnLabel = rule.createdDate
      ? t('organisation.extraction-rules.rule-list-created-on', {
          date: formatDisplayDate(rule.createdDate, 'DD/MM/YYYY', i18n.language),
        })
      : t('organisation.extraction-rules.rule-list-created-on', { date: '-' });

    return (
      <RuleListItem key={rule.id} $enabled={rule.enabled}>
        <RuleListItemHeader>
          <RuleListItemLeft>
            <Switch
              size="small"
              checked={rule.enabled}
              aria-label={`${t('common.enabled')}: ${displayName}`}
              onChange={(checked) => void handleToggle(rule.id, checked)}
            />
            <RuleListItemContent
              type="button"
              onClick={() => openRuleModal(rule, 'build')}
              title={displayName}
            >
              <RuleListItemTextColumn>
                <RuleListItemMetaDate>{createdOnLabel}</RuleListItemMetaDate>
                <Tooltip title={displayName} placement="top">
                  <RuleListItemTitle>{displayName}</RuleListItemTitle>
                </Tooltip>
              </RuleListItemTextColumn>
            </RuleListItemContent>
          </RuleListItemLeft>
          <RuleListItemRight onClick={(e) => e.stopPropagation()}>
            <Popover
              placement="bottomRight"
              arrow={false}
              trigger="click"
              open={openMenuRuleId === rule.id}
              onOpenChange={(open) =>
                setOpenMenuRuleId(open ? rule.id : null)
              }
              // Above DrawerContainer (1200) so the ⋮ menu is clickable in the rules drawer.
              overlayStyle={{ zIndex: 1400 }}
              content={renderRuleContextMenu(rule)}
              destroyOnHidden={true}
            >
              <ContextButton type="text" aria-label={t('common.more-actions')}>
                <EllipseIcon style={{ width: 18, height: 18 }} />
              </ContextButton>
            </Popover>
          </RuleListItemRight>
        </RuleListItemHeader>
      </RuleListItem>
    );
  };

  return (
    <>
      <ExtractionRulesDrawerStyle />
      <DrawerWrapper className="extraction-rules-wrapper">
        <DrawerToolbar>
          <HeaderActions>
            <ToolbarActionButton
              type="primary"
              height="32px"
              width="fit-content"
              icon={<PlusOutlined />}
              onClick={() => openRuleModal(buildEmptyRuleDraft(undefined, t))}
            >
              {t('organisation.extraction-rules.new-rule')}
            </ToolbarActionButton>
          </HeaderActions>
          {rules.length > 0 ? (
            <DrawerStatsBadge>
              {t('organisation.extraction-rules.active-count', {
                count: activeCount,
                total: rules.length,
              })}
            </DrawerStatsBadge>
          ) : null}
        </DrawerToolbar>

        <RulesListPanel>
          <RulesListContainer>
            {hasAnyRules ? (
              <RulesSearchRow>
                <RoundInput
                  prefix={
                    <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
                  }
                  placeholder={t(
                    'organisation.extraction-rules.search-placeholder'
                  )}
                  value={ruleSearchQuery}
                  onChange={(e) => setRuleSearchQuery(e.target.value)}
                  allowClear
                  bg_color="var(--color-surface-raised, #ffffff)"
                  width="100%"
                  height="40px"
                  style={{ fontSize: 13 }}
                />
              </RulesSearchRow>
            ) : null}

            {!hasAnyRules ? (
              <EmptyState>
                <EmptyStateIcon>
                  <IconFuzzyMapping aria-hidden style={{ width: 20, height: 20 }} />
                </EmptyStateIcon>
                <EmptyStateText>
                  {t('organisation.extraction-rules.empty-list')}
                </EmptyStateText>
                <ToolbarActionButton
                  type="primary"
                  height="32px"
                  width="fit-content"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    openRuleModal(buildEmptyRuleDraft(undefined, t))
                  }
                >
                  {t('organisation.extraction-rules.new-rule')}
                </ToolbarActionButton>
              </EmptyState>
            ) : filteredItemCount === 0 ? (
              <RulesSearchNoResults>
                {t('organisation.extraction-rules.search-no-results')}
              </RulesSearchNoResults>
            ) : (
              <RulesListWrapper
                ref={rulesListViewportRef}
                tabIndex={0}
                role="region"
                aria-label={t('organisation.extraction-rules.drawer-title')}
              >
                {paginatedRules.map(renderRuleRow)}
              </RulesListWrapper>
            )}

            {filteredItemCount > RULE_LIST_PAGE_SIZE && (
              <RulesPaginationRow>
                <ScopeButton
                  width="88px"
                  height="25px"
                  fontSize="12px"
                  lineheight="12px"
                  onClick={() =>
                    setRuleListCurrentPage((page) => Math.max(1, page - 1))
                  }
                  type="default"
                  style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
                  disabled={ruleListCurrentPage === 1}
                >
                  {t('main.previous')}
                </ScopeButton>
                <ScopeButton
                  width="75px"
                  height="25px"
                  fontSize="12px"
                  lineheight="12px"
                  onClick={() =>
                    setRuleListCurrentPage((page) =>
                      Math.min(ruleListTotalPages, page + 1)
                    )
                  }
                  type="default"
                  disabled={ruleListCurrentPage >= ruleListTotalPages}
                >
                  {t('main.next')}
                </ScopeButton>
              </RulesPaginationRow>
            )}
          </RulesListContainer>
        </RulesListPanel>
      </DrawerWrapper>
    </>
  );
};
